/**
 * Web API client — timeout on all requests, optional silent JWT refresh.
 * Refresh token is stored in an httpOnly cookie (set by server); access token
 * remains in localStorage for fast reads. CSRF double-submit cookie is read
 * from `csrf_token` (non-httpOnly) and echoed in `x-csrf-token` on mutations.
 */

import {
  fetchRefreshWithRetry,
  refreshWithTransientRetry,
  shouldAttemptLegacyBodyRefresh,
  withWebRefreshLock,
} from '../../shared/webRefreshCoordination.mjs';
import { shouldPreserveWebSessionOn401Decision } from '../../shared/webSessionPreserve401.mjs';
import { redirectToSessionExpiredLogin } from './sessionExpiry';

const API_BASE = import.meta.env.VITE_API_URL || '';
const DEFAULT_TIMEOUT_MS = 30000;
/** Provider activity sync / backfills (Intervals, Strava) — matches mobile `LONG_RUNNING_SYNC_TIMEOUT_MS`. */
export const LONG_RUNNING_SYNC_TIMEOUT_MS = 120000;

/** True when fetch was aborted (client timeout or external signal). Keep in sync with apps/mobile/lib/api.ts. */
export function isFetchAbortError(err) {
  if (!err || typeof err !== 'object') return false;
  return err.name === 'AbortError' || /aborted/i.test(String(err.message ?? ''));
}
const LS_REFRESH = 'refresh_token';
const CSRF_COOKIE_NAME = 'csrf_token';
const CSRF_HEADER_NAME = 'x-csrf-token';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/** Precompiled: hot path on every mutating request (only csrf_token is read today). */
const CSRF_COOKIE_VALUE_RE = /(?:^|; )csrf_token=([^;]*)/;

/** Read a cookie value by name (browser only). */
function getCookie(name) {
  if (typeof document === 'undefined') return null;
  if (name === CSRF_COOKIE_NAME) {
    const match = document.cookie.match(CSRF_COOKIE_VALUE_RE);
    return match ? decodeURIComponent(match[1]) : null;
  }
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1]) : null;
}

export function getWebApiBase() {
  return API_BASE;
}

/**
 * Resolve an absolute API URL. When VITE_API_URL is empty (same-origin deploy),
 * uses window.location.origin in the browser so `new URL()` never throws.
 *
 * @param {string} path - e.g. '/api/activities/live'
 * @returns {string}
 */
export function resolveWebApiUrl(path) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const base = String(API_BASE || '').replace(/\/$/, '');
  if (base) return `${base}${normalizedPath}`;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${normalizedPath}`;
  }
  return normalizedPath;
}

/**
 * Build auth + CSRF headers for raw fetch calls that bypass apiFetch.
 * Prefer apiFetch for new code; use this only for legacy call sites.
 * @param {string} [method='GET']
 * @returns {Record<string, string>}
 */
export function getAuthHeaders(method = 'GET') {
  const headers = {};
  const token = getStoredAccessToken();
  if (token) headers['x-auth-token'] = token;
  if (!SAFE_METHODS.has(method.toUpperCase())) {
    const csrf = getCookie(CSRF_COOKIE_NAME);
    if (csrf) headers[CSRF_HEADER_NAME] = csrf;
  }
  return headers;
}

/**
 * @param {string} url
 * @param {RequestInit & { timeoutMs?: number; externalSignal?: AbortSignal }} [opts]
 */
export function fetchWithTimeout(url, opts = {}) {
  const {
    timeoutMs = DEFAULT_TIMEOUT_MS,
    externalSignal,
    ...fetchInit
  } = opts;
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  const onExternalAbort = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) {
      clearTimeout(id);
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', onExternalAbort, { once: true });
    }
  }
  return fetch(url, { ...fetchInit, signal: controller.signal, credentials: 'include' })
    .catch((err) => {
      if (err && err.name === 'AbortError') {
        const reason =
          externalSignal?.aborted === true ? 'external-abort' : 'timeout';
        const wrapped = new Error(`Request aborted (${reason})`);
        wrapped.name = 'AbortError';
        wrapped.code = reason;
        wrapped.silent = true;
        throw wrapped;
      }
      throw err;
    })
    .finally(() => {
      clearTimeout(id);
      externalSignal?.removeEventListener('abort', onExternalAbort);
    });
}

export function persistWebSession(payload) {
  if (payload.token) localStorage.setItem('token', payload.token);
  if (payload.refreshToken) localStorage.setItem(LS_REFRESH, payload.refreshToken);
  if (payload.user) localStorage.setItem('user', JSON.stringify(payload.user));
}

export function clearWebSession() {
  localStorage.removeItem('token');
  localStorage.removeItem(LS_REFRESH);
  localStorage.removeItem('user');
}

/** @returns {string|null} */
export function getStoredAccessToken() {
  return localStorage.getItem('token');
}

let refreshingPromise = null;

/** @type {{ kind: 'success' } | { kind: 'transient' } | { kind: 'auth-failure' } | null} */
let lastRefreshAttemptOutcome = null;

/** @type {Set<(payload: { user?: object }) => void>} */
const tokenRefreshListeners = new Set();

/**
 * Subscribe to successful access-token refresh (after new tokens are persisted).
 * Use to merge slim `user` from refresh with extended fields already in session.
 * @param {(payload: { user?: object }) => void} listener
 * @returns {() => void} unsubscribe
 */
export function subscribeAccessTokenRefreshed(listener) {
  tokenRefreshListeners.add(listener);
  return () => tokenRefreshListeners.delete(listener);
}

function emitAccessTokenRefreshed(payload) {
  tokenRefreshListeners.forEach((fn) => {
    try {
      fn(payload);
    } catch {
      /* ignore listener errors */
    }
  });
}

async function readResponseCode(res) {
  try {
    const body = await res.clone().json();
    return typeof body?.code === 'string' ? body.code : null;
  } catch {
    return null;
  }
}

/** @param {Response} res */
export async function readAuthErrorCode(res) {
  return readResponseCode(res);
}

async function performRefreshRequest() {
  const url = `${API_BASE}/api/auth/refresh`;
  const baseOpts = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    timeoutMs: DEFAULT_TIMEOUT_MS,
    credentials: 'include',
  };

  // 1) Cookie-only (httpOnly refresh_token)
  let res = await refreshWithTransientRetry(() => fetchWithTimeout(url, { ...baseOpts }));
  let usedLegacyBody = false;

  // 2) Legacy localStorage refresh — never after deploy-style 5xx on cookie path
  if (!res.ok && shouldAttemptLegacyBodyRefresh(res.status, { attemptedCookieRefresh: true })) {
    const legacyToken = localStorage.getItem(LS_REFRESH);
    if (legacyToken) {
      usedLegacyBody = true;
      res = await refreshWithTransientRetry(() =>
        fetchWithTimeout(url, {
          ...baseOpts,
          body: JSON.stringify({ refreshToken: legacyToken }),
        })
      );
    }
  }

  return { res, usedLegacyBody };
}

/** @returns {object|null} Cached user from localStorage (browser only). */
export function readCachedWebUser() {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Whether a refresh credential likely exists (httpOnly cookie and/or legacy LS). */
export function hasWebRefreshCredential() {
  if (typeof localStorage === 'undefined') return false;
  return Boolean(localStorage.getItem('token') || localStorage.getItem(LS_REFRESH));
}

/**
 * After a 401, preserve session when refresh failed transiently during deploy cutover.
 * @param {Response} response
 * @returns {Promise<boolean>}
 */
export async function shouldPreserveWebSessionOn401(response) {
  const code = await readAuthErrorCode(response);
  return shouldPreserveWebSessionOn401Decision({
    status: response?.status ?? 0,
    code,
    refreshOutcomeKind: lastRefreshAttemptOutcome?.kind,
    hasRefreshCredential: hasWebRefreshCredential(),
  });
}

/**
 * Preserve session during deploy cutover; otherwise redirect to login.
 * @param {Response} response
 * @param {{ navigate: Function, location: object, onExpired?: () => void }} handlers
 * @returns {Promise<boolean>} true when session was preserved
 */
export async function handleWebAuth401(response, handlers) {
  if (await shouldPreserveWebSessionOn401(response)) {
    const cachedUser = readCachedWebUser();
    if (cachedUser && handlers.onPreserved) {
      handlers.onPreserved(cachedUser);
    }
    return true;
  }
  if (response?.status === 401 || response?.status === 403) {
    redirectToSessionExpiredLogin({
      navigate: handlers.navigate,
      location: handlers.location,
      onBeforeNavigate: handlers.onExpired,
    });
    return false;
  }
  return false;
}

/** @returns {{ kind: 'success', token: string } | { kind: 'transient' } | { kind: 'auth-failure' }} */
export async function refreshAccessTokenWithOutcome() {
  if (refreshingPromise) return refreshingPromise;

  refreshingPromise = withWebRefreshLock(async () => {
    try {
      const outcome = await fetchRefreshWithRetry(async () => {
        const { res } = await performRefreshRequest();
        return res;
      });

      lastRefreshAttemptOutcome = outcome;

      if (outcome.kind !== 'success') {
        return outcome;
      }

      const legacyRefresh = localStorage.getItem(LS_REFRESH);
      if (legacyRefresh && outcome.refreshToken) {
        persistWebSession({
          token: outcome.token,
          refreshToken: outcome.refreshToken,
          user: outcome.user,
        });
      } else {
        localStorage.removeItem(LS_REFRESH);
        persistWebSession({
          token: outcome.token,
          user: outcome.user,
        });
      }
      if (outcome.user) {
        emitAccessTokenRefreshed({ user: outcome.user });
      }
      return { kind: 'success', token: outcome.token };
    } catch {
      lastRefreshAttemptOutcome = { kind: 'transient' };
      return { kind: 'transient' };
    } finally {
      refreshingPromise = null;
    }
  });

  return refreshingPromise;
}

async function refreshAccessToken() {
  const outcome = await refreshAccessTokenWithOutcome();
  return outcome.kind === 'success' ? outcome.token : null;
}

/** Exchange httpOnly refresh cookie for access token (e.g. after OAuth web redirect). */
export { refreshAccessToken };

/**
 * Authenticated JSON API request with timeout and one 401→refresh retry.
 * @param {string} path - `/api/...` or absolute URL
 * @param {{
 *   method?: string,
 *   body?: unknown,
 *   token?: string|null,
 *   skipRefresh?: boolean,
 *   headers?: Record<string,string>,
 *   signal?: AbortSignal,
 *   timeoutMs?: number,
 *   skipResponseCache?: boolean,
 * }} [opts]
 */
export async function apiFetch(path, opts = {}) {
  const {
    method = 'GET',
    body,
    token: tokenOpt,
    skipRefresh = false,
    headers: extraHeaders = {},
    signal,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    skipResponseCache = false,
  } = opts;

  const token = tokenOpt ?? getStoredAccessToken();
  const headers = { ...extraHeaders };
  if (skipResponseCache && (method === 'GET' || method === 'HEAD')) {
    headers['Cache-Control'] = 'no-cache';
  }
  const hasBody = body !== undefined && body !== null && method !== 'GET' && method !== 'HEAD';
  const isFormDataBody =
    typeof FormData !== 'undefined' && body instanceof FormData;
  if (hasBody && !isFormDataBody && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) headers['x-auth-token'] = token;
  // CSRF double-submit: echo the non-httpOnly csrf_token cookie on mutating requests
  if (!SAFE_METHODS.has(method.toUpperCase())) {
    const csrfToken = getCookie(CSRF_COOKIE_NAME);
    if (csrfToken) headers[CSRF_HEADER_NAME] = csrfToken;
  }

  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  const doFetch = (authToken) =>
    fetchWithTimeout(url, {
      method,
      headers: authToken ? { ...headers, 'x-auth-token': authToken } : headers,
      ...(hasBody
        ? {
            body:
              typeof body === 'string'
                ? body
                : isFormDataBody
                  ? body
                  : JSON.stringify(body),
          }
        : {}),
      timeoutMs,
      externalSignal: signal,
    });

  let res = await doFetch(token);

  if (res.status === 401 && token && !skipRefresh) {
    const code = await readResponseCode(res);
    const shouldAttemptRefresh = code === 'TOKEN_EXPIRED' || code === 'INVALID_TOKEN';
    if (shouldAttemptRefresh) {
      const refreshOutcome = await refreshAccessTokenWithOutcome();
      if (refreshOutcome.kind === 'success') {
        res = await doFetch(refreshOutcome.token);
      }
    }
  }

  return res;
}
